import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  errorsApi,
  type ErrorListParams,
  type UpdateErrorPayload,
} from "@/lib/admin-api/errors";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  list: (params: ErrorListParams) => ["admin-errors", params] as const,
  detail: (id: string) => ["admin-error-detail", id] as const,
  stats: ["admin-error-stats"] as const,
} as const;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useStorefrontErrors(params: ErrorListParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => errorsApi.list(params),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useStorefrontErrorDetail(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => errorsApi.detail(id),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useErrorStats() {
  return useQuery({
    queryKey: KEYS.stats,
    queryFn: errorsApi.stats,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useUpdateError() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateErrorPayload }) =>
      errorsApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-errors"] });
      queryClient.invalidateQueries({ queryKey: ["admin-error-stats"] });
      toast.success("Erreur mise \u00e0 jour");
    },
  });
}

export function useBulkUpdateErrors() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      payload,
    }: {
      ids: readonly string[];
      payload: UpdateErrorPayload;
    }) => errorsApi.bulkUpdate(ids, payload),
    onSuccess: (_data, { ids }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-errors"] });
      queryClient.invalidateQueries({ queryKey: ["admin-error-stats"] });
      toast.success(`${ids.length} erreur(s) mise(s) \u00e0 jour`);
    },
  });
}
